import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import PageHeader from "../components/PageHeader";
import Reveal from "../components/Reveal";
import SmartImage from "../components/SmartImage";
import TeamGrid from "../components/TeamGrid";
import LogoSlider from "../components/LogoSlider";
import Seo from "../components/Seo";
import { useI18n } from "../i18n";
import { useSiteData } from "../siteData";
import { assetUrl } from "../adminApi";
import { SEO_META } from "../seo";

export default function About() {
  const { t, loc } = useI18n();
  const { content } = useSiteData();
  const about = content.about || {};
  const paragraphs = about.paragraphs || [];
  const stats = about.stats || [];
  const team = content.team?.items || about.team || [];
  const process = about.process || [];
  const pages = content.pages || {};
  const txt = (k, f) => loc(pages[k]) || t(f);

  return (
    <div>
      <Seo path="/about" title={loc(SEO_META.about.title)} description={loc(SEO_META.about.description)} />
      <PageHeader
        index={txt("aboutIndex", "about.studio")}
        title={<span className="whitespace-pre-line">{txt("aboutTitle", "about.title")}</span>}
        lead={loc(about.philosophy)}
      />

      <section className="shell py-20 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            {paragraphs.map((p, i) => (
              <Reveal key={i} delay={i * 90}>
                <p
                  className={
                    i === 0
                      ? "text-[19px] lg:text-[23px] leading-[1.55] text-[#141414] mb-7"
                      : "text-[14.5px] lg:text-[15.5px] leading-relaxed text-[#141414]/70 mb-6"
                  }
                >
                  {loc(p)}
                </p>
              </Reveal>
            ))}
          </div>
          <div className="lg:col-span-4 lg:col-start-9">
            <div className="border-t border-[#E2E0D8]">
              {stats.map((s) => (
                <div key={s.value} className="flex items-baseline justify-between py-5 border-b border-[#E2E0D8]">
                  <span className="mono text-[9px] text-[#6B675F]">{loc(s.label)}</span>
                  <span className="display text-[28px] text-[#141414]">{s.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="shell pb-20 lg:pb-28">
        <div className="media aspect-[16/8] border border-[#E2E0D8]">
          <SmartImage
            src={assetUrl(about.studioImage)}
            alt="HRD studio"
            fallbackIndex={8}
            className="w-full h-full object-cover"
          />
        </div>
      </section>

      {/* founding partners · compact portraits */}
      <section className="bg-[#EFECE6] border-t border-b border-[#E2E0D8] py-24 lg:py-32" data-testid="team-section">
        <div className="shell">
          <p className="mono text-[9.5px] text-[#6B675F] mb-12">{txt("aboutPartnersLabel", "about.partners")}</p>
          <TeamGrid members={team} />
        </div>
      </section>

      <LogoSlider page="about" anchor="afterTeam" />

      {/* how we work · needs analysis step removed per brief */}
      <section className="shell py-24 lg:py-32">
        <p className="mono text-[9.5px] text-[#6B675F] mb-10">{txt("aboutHowLabel", "about.how")}</p>
        <div className="border-t border-[#E2E0D8]">
          {process.slice(1).map((p) => (
            <div key={p.no} className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-10 py-8 border-b border-[#E2E0D8]">
              <span className="lg:col-span-1 mono text-[10px] text-[#A8A29A]">{p.no}</span>
              <h3 className="lg:col-span-4 text-[20px] font-medium text-[#141414]">{loc(p.title)}</h3>
              <p className="lg:col-span-7 text-[14.5px] text-[#141414]/70 leading-relaxed">{loc(p.text)}</p>
            </div>
          ))}
        </div>
        <Link
          to="/contact"
          className="mt-12 inline-flex items-center gap-2 bg-[#141414] text-[#F7F6F2] px-6 py-4 mono text-[10px] hover:bg-[#2A2A28] transition-colors duration-300"
        >
          {txt("aboutHowCta", "cta.workWithUs")} <ArrowUpRight size={14} strokeWidth={1.6} />
        </Link>
      </section>

      <LogoSlider page="about" anchor="bottom" />
    </div>
  );
}
